import { ascend, prop, sortWith } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getDomainId } from 'api-client/helpers'
import { blockStorageDoc, ephemeralSharedStorageDoc } from 'app/docLinks'
import useTenantSelector from 'app/plugins/account/components/userManagement/tenants/useTenantSelector'
import { VmStorageOptions } from 'app/plugins/account/components/userManagement/tenants/VmStorageTypePicklist'
import Alert from 'core/components/Alert'
import ExternalLink from 'core/components/ExternalLink'
import PaginatedSelectableCards from 'core/components/PaginatedSelectableCards'
import Progress from 'core/components/progress/Progress'
import SimpleLink from 'core/components/SimpleLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import { default as PicklistField } from 'core/components/validatedForm/DropdownField'
import Info from 'core/components/validatedForm/Info'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import Dropdown from 'core/elements/dropdown'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import { routeManager } from 'core/utils/routes'
import { humanReadableSize } from 'openstack/helpers'
import { formatDate } from 'utils/misc'
import ServiceUnhealthyInfo from '../../common/ServiceUnhealthyInfo'
import { listImages } from '../../images/actions'
import { ImageDetailsPageTabs } from '../../images/details/model'
import { imageColumns } from '../../images/ImagesListPage'
import { imagesSelector } from '../../images/selectors'
import { getBlueprints, listResmgrHosts } from '../../infrastructure/actions'
import ClusterHostGroupsPicklist from '../../infrastructure/clusters/ClusterHostGroupsPicklist'
import { imageLibrayHostSelector } from '../../infrastructure/selectors'
import VolumeTypesPicklist from '../../storage/volume-types/VolumeTypesPicklist'
import { IVolumeDetailsPageTabs } from '../../storage/volumes/volume-details/model'
import { listVirtualMachinesLite } from '../actions'
import {
  createDefaultCreateVmParams,
  getAllowedVmSnapshotTypes,
  getCreateVmSourceTypeOptions,
  getFilteredCreateVmImages,
  getTenantVmStorageType,
  hostnameValidator,
  isCreateVmUsingEphemeralRootDisk,
} from '../CreateVirtualMachineModal.helpers'
import { ErrorText } from '../CreateVirtualMachineModal.shared'
import useCreateVirtualMachineModalStyles from '../CreateVirtualMachineModal.styles'
import {
  getVmSnapshotColumns,
  imageColumnsOrder,
  isImageCompatibleWithVolumeAndFlavor,
  visibleImageColumns,
  visibleVmSnapshotColumns,
  vmSnapshotColumnsOrder,
  volumeColumns,
} from '../helpers'
import SourceTypesPicklist from '../SourceTypesPicklist'
import { virtualMachinesLiteByIdSelector } from '../selectors'
import { VOLUME_BACKED_VM_WARNING_MSG } from '../tooltipConstants'
import SourceImageCard from './SourceImageCard'
import ViewModeToggle from './ViewModeToggle'

const desiredLocalImageColumns = ['name', 'disk_format', 'virtual_size', 'created_at']
const defaultVolumeSize = createDefaultCreateVmParams(false).volumeSize

const calcIsoVolumeSizeGiB = (virtualSizeBytes: number): number =>
  virtualSizeBytes ? Math.ceil(virtualSizeBytes / (1024 * 1024 * 1024)) : 1

const ISO_SORT_ITEMS = [
  { label: 'Select...', value: '' },
  { label: 'Name', value: 'name' },
  { label: 'Virtual Size', value: 'virtualSize' },
]

interface IsoPickerFieldProps {
  id: string
  data: any[]
  loading: boolean
  sortDropdownName: string
  sort: string
  onSortChange: (value: string) => void
  viewToggle: string
  onViewToggleChange: (value: string) => void
  renderCard: (item: any) => JSX.Element
  onChange: (selected: any[]) => void
  value: any[]
  required?: boolean
  imageColumnsLocal: any[]
}

function IsoPickerField({
  id,
  data,
  loading,
  sortDropdownName,
  sort,
  onSortChange,
  viewToggle,
  onViewToggleChange,
  renderCard,
  onChange,
  value,
  required,
  imageColumnsLocal,
}: IsoPickerFieldProps) {
  const classes = useCreateVirtualMachineModalStyles()
  return viewToggle === 'grid' ? (
    <PaginatedSelectableCards
      renderSelectableCardComponent={renderCard}
      data={data}
      searchProps={['name', 'disk_format']}
      pluralLabel="ISOs"
      extraHeaderContent={<ViewModeToggle value={viewToggle} onChange={onViewToggleChange} />}
      sortDropdown={
        <Dropdown
          name={sortDropdownName}
          value={sort}
          items={ISO_SORT_ITEMS}
          onChange={onSortChange}
        />
      }
    />
  ) : (
    <>
      <div className={classes.spaceBelow}>
        <ViewModeToggle value={viewToggle} onChange={onViewToggleChange} />
      </div>
      <ListTableField
        id={id}
        data={data}
        loading={loading}
        columns={imageColumnsLocal}
        visibleColumns={visibleImageColumns}
        columnsOrder={imageColumnsOrder}
        onChange={onChange}
        value={value}
        uniqueIdentifier="id"
        searchTargets={['name', 'disk_format']}
        required={required}
      />
    </>
  )
}

interface SharedSourceResources {
  availableVolumesRaw: any[]
  loadingAvailableVolumes: boolean
  loadingVolumeSnapshots: boolean
  loadingVolumes: boolean
  reloadAvailableVolumes: () => void
  updatingAvailableVolumes: boolean
  volumeSnapshots: any[]
  volumeSnapshotsById: Record<string, any>
  volumesById: Record<string, any>
}

interface SourceControllerInput extends SharedSourceResources {
  currentTenant: string
  isOpen: boolean
  params: any
}

interface Props extends SharedSourceResources {
  allowedVmSnapshotTypes: string[]
  blueprint: any
  bootableVolumesList: any[]
  filteredImages: any[]
  getParamsUpdater: any
  imageColumnsLocal: any[]
  imageSort: string
  imageType: string
  isClusterRequired: boolean
  isImageLibraryHostAvailable: boolean
  isVMUsingEphemeralRootDisk: boolean
  loadingImages: boolean
  loadingResmgrHosts: boolean
  params: any
  setImageSort: (value: string) => void
  setImageType: (value: string) => void
  setViewImageToggle: (value: string) => void
  sourceTypeOptions: Array<{ label: string; value: string }>
  updateParams: any
  viewImageToggle: string
  vmSnapshotColumns: any[]
  volumeSnapshotColumns: any[]
  filteredIsoImages: any[]
  filteredDriverIsoImages: any[]
  installerIsoViewToggle: string
  driverIsoViewToggle: string
  installerIsoSort: string
  driverIsoSort: string
  setInstallerIsoViewToggle: (value: string) => void
  setDriverIsoViewToggle: (value: string) => void
  setInstallerIsoSort: (value: string) => void
  setDriverIsoSort: (value: string) => void
}

export function useCreateVirtualMachineSourceStepController({
  availableVolumesRaw,
  currentTenant,
  isOpen,
  loadingAvailableVolumes,
  loadingVolumeSnapshots,
  loadingVolumes,
  params,
  reloadAvailableVolumes,
  updatingAvailableVolumes,
  volumeSnapshots,
  volumeSnapshotsById,
  volumesById,
}: SourceControllerInput) {
  const [viewImageToggle, setViewImageToggle] = useState('list')
  const [installerIsoViewToggle, setInstallerIsoViewToggle] = useState('list')
  const [driverIsoViewToggle, setDriverIsoViewToggle] = useState('list')
  const [imageSort, setImageSort] = useState('')
  const [installerIsoSort, setInstallerIsoSort] = useState('')
  const [driverIsoSort, setDriverIsoSort] = useState('')
  const [imageType, setImageType] = useState('')
  const [blueprint, setBlueprint] = useState(null)

  const { loading: loadingImages } = useListAction(listImages, {
    params: {},
  })
  const images = useSelector(imagesSelector)

  const { loading: loadingResmgrHosts } = useListAction(listResmgrHosts, {
    params,
  })
  const imageLibraryHost = useSelector(imageLibrayHostSelector)
  const isImageLibraryHostAvailable = !!imageLibraryHost

  useListAction(listVirtualMachinesLite)
  const vmsById = useSelector(virtualMachinesLiteByIdSelector)

  const { data: tenants, reload: reloadTenants } = useTenantSelector(getDomainId())
  const tenantVmStorageType = getTenantVmStorageType(tenants, currentTenant)

  useEffect(() => {
    const queryBlueprint = async () => {
      try {
        const blueprints = await getBlueprints()
        setBlueprint(blueprints?.[0] ?? null)
      } catch {
        setBlueprint(null)
      }
    }

    queryBlueprint()
  }, [])

  const sourceTypeOptions = useMemo(
    () => getCreateVmSourceTypeOptions(tenantVmStorageType),
    [tenantVmStorageType],
  )

  const allowedVmSnapshotTypes = useMemo(
    () => getAllowedVmSnapshotTypes(tenantVmStorageType),
    [tenantVmStorageType],
  )

  const bootableVolumesList = useMemo(
    () => availableVolumesRaw.filter((volume) => volume.bootable !== 'false'),
    [availableVolumesRaw],
  )

  const filteredImages = useMemo(
    () =>
      getFilteredCreateVmImages({
        images,
        imageSort,
        imageType,
        sourceType: params.sourceType,
        tenantVmStorageType,
      }),
    [images, imageSort, imageType, params.sourceType, tenantVmStorageType],
  )

  const isoImages = useMemo(
    () => images.filter((image) => image.status === 'active' && image.disk_format === 'iso'),
    [images],
  )

  const isoSorters = {
    name: sortWith([ascend(prop('name'))]),
    virtualSize: sortWith([ascend(prop('virtual_size'))]),
  }

  const sortIsoImages = (list: any[], sortKey: string) =>
    sortKey && isoSorters[sortKey] ? isoSorters[sortKey](list) : list

  const filteredIsoImages = useMemo(
    () => sortIsoImages(isoImages, installerIsoSort),
    [isoImages, installerIsoSort],
  )

  const filteredDriverIsoImages = useMemo(
    () => sortIsoImages(isoImages, driverIsoSort),
    [isoImages, driverIsoSort],
  )

  const vmSnapshotColumns = useMemo(
    () =>
      getVmSnapshotColumns({
        vmsById,
        volumeSnapshotsById,
      }),
    [vmsById, volumeSnapshotsById],
  )

  const imageColumnsLocal = useMemo(
    () =>
      imageColumns(false)
        .map((col) => ({
          ...col,
          id: col.key,
        }))
        .filter((col) => desiredLocalImageColumns.includes(col.key)),
    [],
  )

  const volumeSnapshotColumns = useMemo(
    () => [
      {
        id: 'name',
        label: 'Name',
      },
      {
        id: 'id',
        label: 'UUID',
        display: false,
      },
      {
        id: 'description',
        label: 'Description',
      },
      {
        id: 'status',
        label: 'Status',
      },
      {
        id: 'size',
        label: 'Size',
        render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
      },
      {
        id: 'volume_id',
        label: 'Source Volume',
        render: (volumeId) => (
          <SimpleLink
            src={routeManager.routes.openstack.volumeDetails.path({
              id: volumeId,
              tab: IVolumeDetailsPageTabs.Overview,
            })}
          >
            {volumesById?.[volumeId]?.name || volumeId}
          </SimpleLink>
        ),
        display: false,
      },
      {
        id: 'attachedVmSnapshot.name',
        label: 'VM Snapshot',
        render: (name, item) => {
          if (!item?.attachedVmSnapshot) {
            return null
          }

          return (
            <SimpleLink
              src={routeManager.routes.openstack.imageDetails.path({
                id: item?.attachedVmSnapshot?.id,
                tab: ImageDetailsPageTabs.Overview,
              })}
            >
              {name}
            </SimpleLink>
          )
        },
        display: false,
      },
      {
        id: 'volumeType',
        label: (
          <TitleWithInfoIcon
            titleText="Volume Type"
            tooltipMsg={<div>This information is inherited from the source volume</div>}
          />
        ),
        render: (_, item) => {
          const volume = volumesById?.[item?.volume_id]
          return volume?.volume_type
        },
      },
      {
        id: 'bootable',
        label: (
          <TitleWithInfoIcon
            titleText="Bootable"
            tooltipMsg={<div>This information is inherited from the source volume</div>}
          />
        ),
        render: (_, item) => {
          const volume = volumesById?.[item?.volume_id]
          return volume?.bootable
        },
      },
      {
        id: 'created_at',
        label: 'Created',
        render: (value) => formatDate(value),
      },
    ],
    [volumesById],
  )

  const isVMUsingEphemeralRootDisk = useMemo(
    () => isCreateVmUsingEphemeralRootDisk(params),
    [params],
  )

  const reloadSourceStepResources = () => {
    if (!isOpen) {
      return
    }

    reloadTenants(true)
  }

  const resetSourceStepState = () => {
    setViewImageToggle('list')
    setInstallerIsoViewToggle('list')
    setDriverIsoViewToggle('list')
    setImageSort('')
    setInstallerIsoSort('')
    setDriverIsoSort('')
    setImageType('')
  }

  return {
    availableVolumesRaw,
    allowedVmSnapshotTypes,
    blueprint,
    bootableVolumesList,
    filteredImages,
    filteredIsoImages,
    filteredDriverIsoImages,
    imageColumnsLocal,
    imageSort,
    imageType,
    images,
    installerIsoViewToggle,
    driverIsoViewToggle,
    installerIsoSort,
    driverIsoSort,
    isImageLibraryHostAvailable,
    isVMUsingEphemeralRootDisk,
    loadingAvailableVolumes,
    loadingImages,
    loadingResmgrHosts,
    loadingVolumeSnapshots,
    loadingVolumes,
    reloadAvailableVolumes,
    reloadSourceStepResources,
    resetSourceStepState,
    setImageSort,
    setInstallerIsoSort,
    setDriverIsoSort,
    setImageType,
    setViewImageToggle,
    setInstallerIsoViewToggle,
    setDriverIsoViewToggle,
    sourceTypeOptions,
    updatingAvailableVolumes,
    viewImageToggle,
    vmSnapshotColumns,
    volumeSnapshotColumns,
    volumeSnapshots,
    volumeSnapshotsById,
    volumesById,
  }
}

export default function CreateVirtualMachineSourceStep({
  allowedVmSnapshotTypes,
  blueprint,
  bootableVolumesList,
  filteredImages,
  getParamsUpdater,
  imageColumnsLocal,
  imageSort,
  imageType,
  isClusterRequired,
  isImageLibraryHostAvailable,
  isVMUsingEphemeralRootDisk,
  loadingAvailableVolumes,
  loadingImages,
  loadingResmgrHosts,
  loadingVolumeSnapshots,
  loadingVolumes,
  params,
  reloadAvailableVolumes,
  setImageSort,
  setImageType,
  setViewImageToggle,
  sourceTypeOptions,
  updateParams,
  updatingAvailableVolumes,
  viewImageToggle,
  vmSnapshotColumns,
  volumeSnapshotColumns,
  volumeSnapshots,
  volumesById,
  filteredIsoImages,
  filteredDriverIsoImages,
  installerIsoViewToggle,
  driverIsoViewToggle,
  installerIsoSort,
  driverIsoSort,
  setInstallerIsoViewToggle,
  setDriverIsoViewToggle,
  setInstallerIsoSort,
  setDriverIsoSort,
}: Props) {
  const classes = useCreateVirtualMachineModalStyles()

  const renderImageCard = useMemo(
    () => (item) => <SourceImageCard item={item} params={params} updateParams={updateParams} />,
    [params, updateParams],
  )

  const renderInstallerIsoCard = useMemo(
    () => (item) => (
      <SourceImageCard
        item={item}
        params={{ ...params, image: params.installerIso }}
        updateParams={(val) => {
          const selected = val.image
          const minGiB = calcIsoVolumeSizeGiB(selected?.[0]?.virtual_size || 0)
          const currentGiB = Number(params.volumeSize) || 50
          const updates: any = { installerIso: selected }
          if (currentGiB < minGiB) {
            updates.volumeSize = String(minGiB)
          }
          updateParams(updates)
        }}
      />
    ),
    [params, updateParams],
  )

  const renderDriverIsoCard = useMemo(
    () => (item) => (
      <SourceImageCard
        item={item}
        params={{ ...params, image: params.driverIso }}
        updateParams={(val) => updateParams({ driverIso: val.image })}
      />
    ),
    [params, updateParams],
  )

  const installerIsoSizeBytes = params.installerIso?.[0]?.virtual_size || 0
  const minVolumeSizeGiB = calcIsoVolumeSizeGiB(installerIsoSizeBytes)
  const currentVolumeSizeGiB = Number(params.volumeSize) || 50
  const isVolumeTooSmall = installerIsoSizeBytes > 0 && currentVolumeSizeGiB < minVolumeSizeGiB

  // Validation function for ISO volume size (prevents form submission)
  const validateIsoVolumeSize = useCallback(() => {
    if (params.sourceType !== 'installFromIso') {
      return true
    }
    if (installerIsoSizeBytes === 0) {
      return true // No ISO selected, no validation needed
    }
    return !isVolumeTooSmall // Returns false if volume is too small
  }, [params.sourceType, installerIsoSizeBytes, isVolumeTooSmall])

  return (
    <div className={classes.fields}>
      <ServiceUnhealthyInfo action="VM Creation" hypervisor imageLibrary blockStorage />
      <Info className={classes.info}>
        <div className={classes.infoContent}>
          <Text variant="caption1">Basic VM Info</Text>
          <Text variant="body2">
            Create a new virtual machine by selecting the desired VM source and target cluster.
          </Text>
        </div>
      </Info>
      <div className={classes.halfColumns}>
        <TextField
          id="name"
          label="Name"
          onChange={getParamsUpdater('name')}
          value={params.name}
          info="Choose a name for your VM"
          required
        />
        <TextField
          id="hostname"
          label="Guest OS Hostname (Optional)"
          onChange={getParamsUpdater('hostname')}
          value={params.hostname}
          validations={[hostnameValidator]}
          info="Hostname for the guest OS. If creating multiple VMs, each will get hostname-<index> suffix"
        />
      </div>
      <TextField
        className={classes.fullWidth}
        id="description"
        label="Description"
        onChange={getParamsUpdater('description')}
        value={params.description}
      />
      <div className={classes.halfColumns}>
        <PicklistField
          id="availabilityZone"
          label="Cluster"
          info="Choose the virtualized cluster that this VM should be placed on"
          onChange={getParamsUpdater('availabilityZone')}
          value={params.availabilityZone}
          disableClusterWithoutHosts
          fullWidth
          required={isClusterRequired}
          DropdownComponent={ClusterHostGroupsPicklist}
          autoSelectFirstValue
        />
        <PicklistField
          DropdownComponent={SourceTypesPicklist}
          id="sourceType"
          label="Boot VM From"
          onChange={(value) => {
            if (value === 'installFromIso') {
              setInstallerIsoViewToggle('list')
              setDriverIsoViewToggle('list')
            }
            setInstallerIsoSort('')
            setDriverIsoSort('')
            updateParams({
              sourceType: value,
              image: [],
              attachVolumes: [],
              volume: [],
              installerIso: [],
              driverIso: [],
              enableDriverIso: false,
              volumeSize: value === 'installFromIso' ? '50' : defaultVolumeSize,
              volumeType: '',
            })
          }}
          value={params.sourceType}
          options={sourceTypeOptions}
          required
        />
      </div>
      {params.sourceType === 'volumeSnapshot' && (
        <ToggleSwitchField
          id="deleteOnTermination"
          value={params.deleteOnTermination}
          onChange={getParamsUpdater('deleteOnTermination')}
          label="Delete Volume on VM deletion"
          customTooltipInfoBody={
            <Text variant="body2" className={classes.switchTooltip}>
              If enabled, the VM boot volume will be deleted when the VM is deleted. If disabled,
              the volume will remain after the VM is deleted.
            </Text>
          }
        />
      )}
      {isVMUsingEphemeralRootDisk ? (
        <>
          {!blueprint?.instanceSharedStorage && (
            <Alert variant="warning" className={classes.warning}>
              <Text variant="caption1">High Availability Warning</Text>
              <Text variant="body2">
                This VM configuration uses ephemeral storage for the root disk. If a host fails, the
                High Availability service cannot recover the data on this VM. For data recovery
                protection, use{' '}
                <ExternalLink url={blockStorageDoc}>Block Storage Volumes</ExternalLink> or{' '}
                <ExternalLink url={ephemeralSharedStorageDoc}>
                  Ephemeral Shared Storage
                </ExternalLink>{' '}
                for the root disk.
              </Text>
            </Alert>
          )}
          <Alert variant="warning" className={classes.warning}>
            <Text variant="body2">{VOLUME_BACKED_VM_WARNING_MSG}</Text>
          </Alert>
        </>
      ) : null}
      {['newVolume', 'installFromIso'].includes(params.sourceType) && (
        <>
          <div className={classes.halfColumns}>
            <TextField
              id="volumeSize"
              label="Volume Size (GiB)"
              onChange={(value) => {
                getParamsUpdater('volumeSize')(value)
                if (
                  !isImageCompatibleWithVolumeAndFlavor(params.image, {
                    ...params,
                    volumeSize: value,
                  })
                ) {
                  updateParams({
                    image: [],
                  })
                }
              }}
              value={params.volumeSize}
              info="Size of the primary VM volume"
              type="number"
              min={0}
              integer
              required
              validations={[
                {
                  validate: validateIsoVolumeSize,
                  errorMessage: `Volume size must be at least ${minVolumeSizeGiB} GiB to fit the selected ISO image.`,
                },
              ]}
            />
            <PicklistField
              id="volumeType"
              DropdownComponent={VolumeTypesPicklist}
              value={params.volumeType}
              onChange={getParamsUpdater('volumeType')}
              required
            />
          </div>
          <ToggleSwitchField
            id="deleteOnTermination"
            value={params.deleteOnTermination}
            onChange={getParamsUpdater('deleteOnTermination')}
            label="Delete Volume on VM deletion"
            customTooltipInfoBody={
              <Text variant="body2" className={classes.switchTooltip}>
                If enabled, the VM boot volume will be deleted when the VM is deleted. If disabled,
                the volume will remain after the VM is deleted.
              </Text>
            }
          />
          {params.sourceType === 'installFromIso' && (
            <Text variant="body2">
              <b>Please note:</b> a new volume will be created with the specified size for the OS
              installation.
            </Text>
          )}
        </>
      )}
      {['image', 'snapshot', 'newVolume'].includes(params.sourceType) && (
        <>
          <div className={classes.separator} />
          <Text variant="subtitle2">Image</Text>
          {isImageLibraryHostAvailable ? (
            <>
              <Text variant="body2">
                Only images that are fully available and ready to use will be shown below.
              </Text>
              <Progress overlay loading={loadingResmgrHosts} message="Loading...">
                {viewImageToggle === 'grid' && (
                  <PaginatedSelectableCards
                    renderSelectableCardComponent={renderImageCard}
                    data={filteredImages}
                    searchProps={['name', 'disk_format']}
                    pluralLabel="Images"
                    extraHeaderContent={
                      <ViewModeToggle value={viewImageToggle} onChange={setViewImageToggle} />
                    }
                    sortDropdown={
                      <Dropdown
                        name="sort"
                        value={imageSort}
                        items={[
                          {
                            label: 'Select...',
                            value: '',
                          },
                          {
                            label: 'Name',
                            value: 'name',
                          },
                          {
                            label: 'Virtual Size',
                            value: 'virtualSize',
                          },
                        ]}
                        onChange={(value) => setImageSort(value)}
                      />
                    }
                  />
                )}
                {viewImageToggle === 'list' && (
                  <>
                    <div className={classes.spaceBelow}>
                      <ViewModeToggle value={viewImageToggle} onChange={setViewImageToggle} />
                      {params.sourceType === 'snapshot' && allowedVmSnapshotTypes.length > 1 && (
                        <Dropdown
                          label="Filter By Type:"
                          compact
                          showAll
                          allKey=""
                          items={allowedVmSnapshotTypes.map((type) => ({
                            value: type,
                          }))}
                          value={imageType}
                          onChange={(value) => setImageType(value)}
                        />
                      )}
                    </div>
                    <ListTableField
                      id="image"
                      data={filteredImages}
                      loading={loadingImages}
                      columns={
                        params.sourceType === 'snapshot' ? vmSnapshotColumns : imageColumnsLocal
                      }
                      visibleColumns={
                        params.sourceType === 'snapshot'
                          ? visibleVmSnapshotColumns
                          : visibleImageColumns
                      }
                      columnsOrder={
                        params.sourceType === 'snapshot'
                          ? vmSnapshotColumnsOrder
                          : imageColumnsOrder
                      }
                      onChange={getParamsUpdater('image')}
                      checkboxCond={(image) => !isImageCompatibleWithVolumeAndFlavor(image, params)}
                      checkboxCondMessage="Image size is larger than the volume size specified"
                      value={params.image}
                      uniqueIdentifier="id"
                      searchTargets={['name', 'disk_format', 'imageTypeDisplay']}
                      required
                    />
                  </>
                )}
              </Progress>
            </>
          ) : (
            <ErrorText>No image library found. Choose a different source</ErrorText>
          )}
        </>
      )}
      {params.sourceType === 'installFromIso' && (
        <>
          <div className={classes.separator} />
          <Text variant="subtitle2">Installation ISO</Text>
          <Text variant="body2">
            Create a new VM and install an operating system from an ISO image.
          </Text>
          {!isImageLibraryHostAvailable ? (
            <ErrorText>No image library found. Choose a different source</ErrorText>
          ) : filteredIsoImages.length === 0 ? (
            <ErrorText>
              No ISO images are available. Upload one on{' '}
              <SimpleLink src={routeManager.routes.openstack.images.path()}>
                the Images page
              </SimpleLink>
              .
            </ErrorText>
          ) : (
            <Progress overlay loading={loadingImages || loadingResmgrHosts} message="Loading...">
              <IsoPickerField
                id="installerIso"
                data={filteredIsoImages}
                loading={loadingImages}
                sortDropdownName="installer-sort"
                sort={installerIsoSort}
                onSortChange={setInstallerIsoSort}
                viewToggle={installerIsoViewToggle}
                onViewToggleChange={setInstallerIsoViewToggle}
                renderCard={renderInstallerIsoCard}
                onChange={(selected) => {
                  const minGiB = calcIsoVolumeSizeGiB(selected?.[0]?.virtual_size || 0)
                  const updates: any = { installerIso: selected }
                  if (currentVolumeSizeGiB < minGiB) {
                    updates.volumeSize = String(minGiB)
                  }
                  updateParams(updates)
                }}
                value={params.installerIso}
                required
                imageColumnsLocal={imageColumnsLocal}
              />
            </Progress>
          )}
          <div className={classes.separator} />
          <ToggleSwitchField
            id="enableDriverIso"
            value={params.enableDriverIso}
            onChange={(value) => {
              getParamsUpdater('enableDriverIso')(value)
              if (!value) {
                updateParams({ driverIso: [] })
              }
            }}
            label="Drivers ISO (optional)"
            textDescription="For Windows installations, VirtIO drivers are recommended for optimal performance."
          />
          {params.enableDriverIso && (
            <>
              {!isImageLibraryHostAvailable ? (
                <ErrorText>No image library found. Choose a different source</ErrorText>
              ) : filteredDriverIsoImages.length === 0 ? (
                <ErrorText>No ISO images are available for the optional driver mount.</ErrorText>
              ) : (
                <Progress
                  overlay
                  loading={loadingImages || loadingResmgrHosts}
                  message="Loading..."
                >
                  <IsoPickerField
                    id="driverIso"
                    data={filteredDriverIsoImages}
                    loading={loadingImages}
                    sortDropdownName="driver-sort"
                    sort={driverIsoSort}
                    onSortChange={setDriverIsoSort}
                    viewToggle={driverIsoViewToggle}
                    onViewToggleChange={setDriverIsoViewToggle}
                    renderCard={renderDriverIsoCard}
                    onChange={getParamsUpdater('driverIso')}
                    value={params.driverIso}
                    imageColumnsLocal={imageColumnsLocal}
                  />
                </Progress>
              )}
            </>
          )}
        </>
      )}
      {params.sourceType === 'existingVolume' && (
        <>
          <div className={classes.separator} />
          <Text variant="subtitle2">Volume</Text>
          <Text variant="body2">
            Only <b>bootable</b> volumes with <b>available</b> status may be selected below.
          </Text>
          <ListTableField
            id="volume"
            data={bootableVolumesList}
            loading={loadingAvailableVolumes || updatingAvailableVolumes}
            onReload={reloadAvailableVolumes}
            columns={volumeColumns}
            onChange={getParamsUpdater('volume')}
            value={params.volume}
            uniqueIdentifier="id"
            searchTargets={['name', 'status', 'volume_type', 'description']}
            checkboxCond={(volume) => volume.status === 'available' && volume.bootable === 'true'}
            required
          />
        </>
      )}
      {params.sourceType === 'volumeSnapshot' && (
        <>
          <div className={classes.separator} />
          <Text variant="subtitle2">Volume Snapshot</Text>
          <Text variant="body2">
            Only <b>bootable</b> volumes snapshots with <b>available</b> status may be selected
            below.
          </Text>
          <ListTableField
            id="volumeSnapshot"
            data={volumeSnapshots}
            loading={loadingVolumeSnapshots || loadingVolumes}
            columns={volumeSnapshotColumns}
            onChange={getParamsUpdater('volumeSnapshot')}
            value={params.volumeSnapshot}
            uniqueIdentifier="id"
            searchTargets={['name', 'description', 'status']}
            checkboxCond={(volumeSnapshot) =>
              volumeSnapshot.status === 'available' &&
              volumesById[volumeSnapshot?.volume_id]?.bootable === 'true'
            }
            required
          />
        </>
      )}
    </div>
  )
}
